import { AnchorItem } from '@/features/ap_payees/components/PayeeDetailDrawer/hooks/anchor/types';
import { useEffect, useRef } from 'react';
import { DEFAULT_ANCHOR } from './anchorData';
import { CLICK_DISABLE_DURATION } from './constants';
import { getAllAnchorHrefs, updateUrlHash } from './utils';

export const useScrollDetection = (
  contentWrapperRef: React.RefObject<HTMLDivElement>,
  isClickingRef: React.MutableRefObject<boolean>,
  isInitializingRef: React.MutableRefObject<boolean>,
  setCurrentAnchor: (anchor: string) => void,
  anchorItems: AnchorItem[]
) => {
  // Cache for performance
  const cacheRef = useRef<{
    rafId: number | null;
    lastAnchor: string | null;
  }>({
    rafId: null,
    lastAnchor: null,
  });

  // Detect scroll and update current anchor
  useEffect(() => {
    // Wait a bit for initialization to complete before setting up scroll detection
    const setupScrollDetection = () => {
      const scrollContainer =
        contentWrapperRef.current?.closest('.ant-drawer-body');
      if (!scrollContainer || !(scrollContainer instanceof HTMLElement)) {
        // Retry if drawer not ready yet
        setTimeout(setupScrollDetection, 50);
        return;
      }

      // Wait for initialization to complete
      if (isInitializingRef.current) {
        setTimeout(setupScrollDetection, 100);
        return;
      }

      // Simple scroll handler: divide scroll height equally among anchors
      const processScroll = () => {
        cacheRef.current.rafId = null;

        // Skip during initialization or clicking
        if (
          isClickingRef.current ||
          isInitializingRef.current ||
          !contentWrapperRef.current
        ) {
          return;
        }

        const allHrefs = getAllAnchorHrefs(anchorItems);
        if (allHrefs.length === 0) {
          return;
        }

        // Calculate total scrollable height
        const scrollHeight = scrollContainer.scrollHeight;
        const clientHeight = scrollContainer.clientHeight;
        const currentScrollTop = scrollContainer.scrollTop;
        const totalScrollableHeight = scrollHeight - clientHeight;

        // If no scrollable content, use first anchor
        if (totalScrollableHeight <= 0) {
          const activeAnchor = allHrefs[0] ?? DEFAULT_ANCHOR;
          if (cacheRef.current.lastAnchor !== activeAnchor) {
            cacheRef.current.lastAnchor = activeAnchor;
            updateUrlHash(activeAnchor);
            setCurrentAnchor(activeAnchor);
          }
          return;
        }

        // Divide scroll height equally among anchors
        const sectionHeight = totalScrollableHeight / allHrefs.length;

        // Determine which section the current scroll position falls into
        const activeIndex = Math.min(
          Math.floor(currentScrollTop / sectionHeight),
          allHrefs.length - 1
        );

        const activeAnchor = allHrefs[activeIndex] ?? DEFAULT_ANCHOR;

        // Only update if anchor changed
        if (cacheRef.current.lastAnchor !== activeAnchor) {
          cacheRef.current.lastAnchor = activeAnchor;
          updateUrlHash(activeAnchor);
          setCurrentAnchor(activeAnchor);
        }
      };

      // Throttle scroll events using RAF (prevents lag on Safari)
      const handleScroll = () => {
        // Skip if already processing or during initialization/clicking
        if (
          isClickingRef.current ||
          isInitializingRef.current ||
          !contentWrapperRef.current
        ) {
          return;
        }

        // Throttle using RAF - only schedule if not already scheduled
        if (cacheRef.current.rafId === null) {
          cacheRef.current.rafId = requestAnimationFrame(processScroll);
        }
      };

      scrollContainer.addEventListener('scroll', handleScroll, {
        passive: true,
      });

      return () => {
        if (cacheRef.current.rafId !== null) {
          cancelAnimationFrame(cacheRef.current.rafId);
          cacheRef.current.rafId = null;
        }
        scrollContainer.removeEventListener('scroll', handleScroll);
      };
    };

    // Start setup after a longer delay to let initialization run first and complete
    const timeoutId = setTimeout(
      setupScrollDetection,
      CLICK_DISABLE_DURATION + 300
    );

    return () => {
      clearTimeout(timeoutId);
      if (cacheRef.current.rafId !== null) {
        cancelAnimationFrame(cacheRef.current.rafId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
        cacheRef.current.rafId = null;
      }
    };
  }, [
    contentWrapperRef,
    anchorItems,
    isInitializingRef,
    isClickingRef,
    setCurrentAnchor,
  ]);
};
